/**
 * Легальность азартных игр по странам (interactives.md, раздел E и
 * «Данные, которые надо добавить»).
 *
 * Три класса по положению жителей страны (не туристов):
 *   legal      — наземные казино лицензированы и открыты для жителей
 *                (пусть и в отдельных зонах, как в России или Казахстане);
 *   restricted — казино для жителей закрыты или их нет, но есть легальная
 *                лотерея и/или ставки (гос. монополия, «только для
 *                иностранцев», запрет казино при разрешённой лотерее);
 *   illegal    — любые азартные игры запрещены законом (в игре остаются
 *                лишь уличные кости и подпольная лотерея с риском ареста).
 *
 * Это не юридическая справка, а грубая игровая классификация: страна с
 * частичными запретами по регионам (Индия, США) отнесена по преобладающему
 * режиму. Таблица составлена вручную по открытым обзорам регулирования
 * (см. GAMBLING_LAW_SOURCE); страна, которой в таблице нет, трактуется как
 * `restricted` с пометкой `estimate` — казино закрыто, лотерея открыта.
 */
import type { Flag } from './schema';

export type GamblingLaw = 'legal' | 'restricted' | 'illegal';

export interface GamblingLawValue {
  law: GamblingLaw;
  flag: Flag;
}

/** Источник таблицы: открытые обзоры регулирования, сведены вручную. */
export const GAMBLING_LAW_SOURCE = {
  title: 'Открытые обзоры регулирования азартных игр по странам (Wikipedia «Gambling by country», World Casino Directory, обзоры национальных регуляторов)',
  url: 'https://en.wikipedia.org/wiki/Gambling_by_country',
  date: '2026-10-22',
} as const;

/** ISO3 → класс легальности. Порядок: по регионам, внутри — по алфавиту. */
export const GAMBLING_LAW: Readonly<Record<string, GamblingLaw>> = {
  // Европа: казино лицензированы почти везде; исключения — монополии без казино.
  ALB: 'restricted',
  AND: 'legal',
  ARM: 'legal',
  AUT: 'legal',
  AZE: 'restricted',
  BEL: 'legal',
  BGR: 'legal',
  BIH: 'legal',
  BLR: 'legal',
  CHE: 'legal',
  CYP: 'legal',
  CZE: 'legal',
  DEU: 'legal',
  DNK: 'legal',
  ESP: 'legal',
  EST: 'legal',
  FIN: 'legal',
  FRA: 'legal',
  GBR: 'legal',
  GEO: 'legal',
  GRC: 'legal',
  HRV: 'legal',
  HUN: 'legal',
  IRL: 'legal',
  ISL: 'restricted',
  ITA: 'legal',
  LIE: 'legal',
  LTU: 'legal',
  LUX: 'legal',
  LVA: 'legal',
  MCO: 'legal',
  MDA: 'legal',
  MKD: 'legal',
  MLT: 'legal',
  MNE: 'legal',
  NLD: 'legal',
  NOR: 'restricted',
  POL: 'legal',
  PRT: 'legal',
  ROU: 'legal',
  RUS: 'legal',
  SMR: 'legal',
  SRB: 'legal',
  SVK: 'legal',
  SVN: 'legal',
  SWE: 'legal',
  TUR: 'restricted',
  UKR: 'legal',
  XKX: 'restricted',

  // Ближний Восток и Северная Африка.
  ARE: 'restricted',
  BHR: 'illegal',
  DZA: 'restricted',
  EGY: 'restricted',
  IRN: 'illegal',
  IRQ: 'illegal',
  ISR: 'restricted',
  JOR: 'illegal',
  KWT: 'illegal',
  LBN: 'legal',
  LBY: 'illegal',
  MAR: 'legal',
  OMN: 'illegal',
  PSE: 'illegal',
  QAT: 'illegal',
  SAU: 'illegal',
  SYR: 'illegal',
  TUN: 'restricted',
  YEM: 'illegal',

  // Центральная и Южная Азия.
  AFG: 'illegal',
  BGD: 'illegal',
  BTN: 'illegal',
  IND: 'restricted',
  KAZ: 'legal',
  KGZ: 'restricted',
  LKA: 'legal',
  MDV: 'illegal',
  NPL: 'restricted',
  PAK: 'illegal',
  TJK: 'illegal',
  TKM: 'illegal',
  UZB: 'restricted',

  // Восточная и Юго-Восточная Азия.
  BRN: 'illegal',
  CHN: 'restricted',
  HKG: 'restricted',
  IDN: 'illegal',
  JPN: 'restricted',
  KHM: 'restricted',
  KOR: 'legal',
  LAO: 'restricted',
  MAC: 'legal',
  MMR: 'restricted',
  MNG: 'illegal',
  MYS: 'legal',
  PHL: 'legal',
  PRK: 'illegal',
  SGP: 'legal',
  THA: 'restricted',
  TLS: 'restricted',
  TWN: 'restricted',
  VNM: 'restricted',

  // Африка южнее Сахары.
  AGO: 'legal',
  BDI: 'restricted',
  BEN: 'legal',
  BFA: 'restricted',
  BWA: 'legal',
  CAF: 'restricted',
  CIV: 'legal',
  CMR: 'legal',
  COD: 'legal',
  COG: 'legal',
  COM: 'restricted',
  CPV: 'legal',
  DJI: 'restricted',
  ERI: 'illegal',
  ETH: 'restricted',
  GAB: 'legal',
  GHA: 'legal',
  GIN: 'restricted',
  GMB: 'restricted',
  GNB: 'restricted',
  GNQ: 'legal',
  KEN: 'legal',
  LBR: 'restricted',
  LSO: 'legal',
  MDG: 'legal',
  MLI: 'restricted',
  MOZ: 'legal',
  MRT: 'illegal',
  MUS: 'legal',
  MWI: 'legal',
  NAM: 'legal',
  NER: 'restricted',
  NGA: 'legal',
  RWA: 'legal',
  SDN: 'illegal',
  SEN: 'legal',
  SLE: 'legal',
  SOM: 'illegal',
  SSD: 'restricted',
  STP: 'restricted',
  SWZ: 'legal',
  SYC: 'legal',
  TCD: 'restricted',
  TGO: 'legal',
  TZA: 'legal',
  UGA: 'legal',
  ZAF: 'legal',
  ZMB: 'legal',
  ZWE: 'legal',

  // Америка.
  ARG: 'legal',
  ATG: 'legal',
  BHS: 'restricted',
  BLZ: 'legal',
  BOL: 'legal',
  BRA: 'restricted',
  BRB: 'restricted',
  CAN: 'legal',
  CHL: 'legal',
  COL: 'legal',
  CRI: 'legal',
  CUB: 'illegal',
  DMA: 'restricted',
  DOM: 'legal',
  ECU: 'restricted',
  GRD: 'legal',
  GTM: 'restricted',
  GUY: 'legal',
  HND: 'legal',
  HTI: 'legal',
  JAM: 'legal',
  KNA: 'legal',
  LCA: 'legal',
  MEX: 'legal',
  NIC: 'legal',
  PAN: 'legal',
  PER: 'legal',
  PRI: 'legal',
  PRY: 'legal',
  SLV: 'restricted',
  SUR: 'legal',
  TTO: 'legal',
  URY: 'legal',
  USA: 'legal',
  VCT: 'restricted',
  VEN: 'restricted',

  // Океания.
  AUS: 'legal',
  FJI: 'restricted',
  FSM: 'restricted',
  KIR: 'restricted',
  NZL: 'legal',
  PNG: 'legal',
  SLB: 'legal',
  TON: 'restricted',
  VUT: 'legal',
  WSM: 'restricted',
};

/** Пустой/неизвестный ISO3 → restricted с пометкой estimate (казино закрыто, лотерея открыта). */
export function gamblingLaw(iso3: string): GamblingLawValue {
  const law = GAMBLING_LAW[iso3];
  if (law === undefined) return { law: 'restricted', flag: 'estimate' };
  return { law, flag: 'fact' };
}
